import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Bell, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import RightSlideModal from '../../components/RightSlideModal';

export default function AnnouncementsPage() {
  const { user } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedAnn, setSelectedAnn] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', targetRole: '' });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await api.get('/announcements');
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedAnn) {
        await api.put(`/announcements/${selectedAnn.id}`, formData);
        toast.success('Announcement updated successfully');
      } else {
        await api.post('/announcements', formData);
        toast.success('Announcement posted successfully');
      }
      setShowModal(false);
      setFormData({ title: '', content: '', targetRole: '' });
      setSelectedAnn(null);
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to save announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted successfully');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete announcement');
    }
  };

  const isTeacherOrAdmin = user?.role === 'admin' || user?.role === 'teacher';

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-slate-800">Announcements</h2>
          <p className="text-slate-500">{isTeacherOrAdmin ? 'Post announcements for students and staff' : 'View important announcements'}</p>
        </div>
        {isTeacherOrAdmin && (
          <button onClick={() => { setSelectedAnn(null); setFormData({ title: '', content: '', targetRole: '' }); setShowModal(true); }} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm active:scale-95 transition-all flex items-center gap-2">
            <Plus className="w-5 h-5" />
            New Announcement
          </button>
        )}
      </div>

      {announcements.length > 0 ? (
        <div className="space-y-4">
          {announcements.map(announcement => (
            <div key={announcement.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Bell className="w-6 h-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-outfit font-semibold text-lg text-slate-800">{announcement.title}</h3>
                      {announcement.targetRole && (
                        <span className="inline-block mt-1 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full capitalize">
                          For: {announcement.targetRole}
                        </span>
                      )}
                    </div>
                    {isTeacherOrAdmin && (
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={() => { setSelectedAnn(announcement); setFormData({ title: announcement.title, content: announcement.content, targetRole: announcement.targetRole || '' }); setShowModal(true); }} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(announcement.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="text-slate-600 mb-3">{announcement.content}</p>
                  <p className="text-sm text-slate-400">
                    {new Date(announcement.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center">
          <Bell className="w-16 h-16 text-indigo-200 mx-auto mb-4" />
          <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-2">No Announcements</h3>
          <p className="text-slate-500 mb-4">{isTeacherOrAdmin ? 'Post your first announcement' : 'No announcements have been posted yet'}</p>
        </div>
      )}

      <RightSlideModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title={`${selectedAnn ? 'Edit' : 'New'} Announcement`}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Holiday Notice" className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Message</label>
            <textarea value={formData.content} onChange={(e) => setFormData({...formData, content: e.target.value})} placeholder="Announcement message..." className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none" rows="4" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Target Audience</label>
            <select value={formData.targetRole} onChange={(e) => setFormData({...formData, targetRole: e.target.value})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 transition-all outline-none">
              <option value="">Everyone</option>
              <option value="teacher">Teachers Only</option>
              <option value="student">Students Only</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-2.5 font-medium">{selectedAnn ? 'Update' : 'Post'} Announcement</button>
            <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">Cancel</button>
          </div>
        </form>
      </RightSlideModal>
    </div>
  );
}
